import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/zoom'

import {
  CloudDownload as DownloadIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material'
import {FC, Fragment, useContext, useMemo, useState} from 'react'
import {useTranslate} from 'react-admin'
import {Navigation, Zoom} from 'swiper/modules'
import {Swiper, SwiperSlide} from 'swiper/react'
import {makeStyles} from 'tss-react/mui'

import ApiUrlsContext from '../../contexts/ApiUrlsContext'
import useDownloadFileAttachments from '../../hooks/useDownloadFileAttachments'
import useIsDesktop from '../../hooks/useIsDesktop'
import {ChatMessageFileAttachments} from '../../types/graphqlSchema'
import {isNativeIosApp} from '../../utils/platform'
import DialogCloseButton from '../DialogCloseButton'
import ExtendedDialog from '../ExtendedDialog'
import RichTranslationText from '../RichTranslationText'

const FileAttachmentsSwiperDialog: FC<FileAttachmentsSwiperDialogProps> = ({
  chatMessageFileAttachments, isOpen, onOpen, value: a,
}) => {
  const {classes} = useStyles({})
  const isDesktop = useIsDesktop()
  const [
    currentAttachment, setCurrentAttachment,
  ] = useState<ChatMessageFileAttachments>(a)
  const [isFullscreen, setFullscreen] = useState(!isDesktop)
  const {getFilesApiUrl} = useContext(ApiUrlsContext)
  const {download, isDownloading} = useDownloadFileAttachments()
  const initialIndex = useMemo(
    () => chatMessageFileAttachments.findIndex(attachment => attachment.id === a.id),
    [a, chatMessageFileAttachments]
  )
  const [activeIndex, setActiveIndex] = useState(0)
  const translate = useTranslate()
  return (
    <ExtendedDialog
      className={classes.root}
      fullScreen={isFullscreen}
      onClose={() => onOpen(false)}
      open={isOpen}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Typography className={classes.title}>
          {currentAttachment?.filename ?? a.filename}
        </Typography>
        <IconButton
          className={classes.fullscreenButton}
          onClick={() => setFullscreen(f => !f)}
        >
          {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
        </IconButton>
        <DialogCloseButton
          className={classes.closeDialogButton}
          onClick={() => onOpen(false)}
        />
      </DialogTitle>
      <DialogContent>
        {
          // TODO: This is a temporary fix for iOS and safari, where .ogg file formats
          //       are not supported
          // @ts-ignore
          (!!window.safari || isNativeIosApp) &&
          (/audio\/ogg/).test(a.mimeType) &&
          <Alert severity="warning">
            <RichTranslationText
              rootElement={Fragment}
              tags={{
                a: match => (
                  <a onClick={() => download([a])}>
                    {match}
                  </a>
                ),
              }}
              translationKey={'chat.chatMessageBubble.chatMessageFileAttachmentList.' +
                'chatMessageFileAttachment.downloadAudioMessage'
              }
            />
          </Alert>
        }
        {/*
            Only once all file attachments are loaded, `initialSlide` can be assigned
            the right prop value. However `<Swiper />` doesn't re-initialize since it
            has already rendered before all attachments were loaded. That's why `key`
            is being reset to instruct React to remount this component with the new
            initial value. See also: https://stackoverflow.com/a/48451229/543875
         */}
        <Swiper
          centeredSlides={true}
          className={classes.swiper}
          initialSlide={initialIndex}
          key={chatMessageFileAttachments.length === 1 ?
            'attachments-loading-swiper' :
            'attachments-loaded-swiper'}
          // @ts-ignore
          lazy={{
            enabled: true,
            loadPrevNext: true,
            loadPrevNextAmount: SWIPER_LOAD_NEXT_PREV_AMOUNT,
          }}
          modules={[Navigation, Zoom]}
          navigation
          onSlideChange={({activeIndex}) => {
            setActiveIndex(activeIndex)
            setCurrentAttachment(chatMessageFileAttachments[activeIndex])
          }}
          slidesPerView={1}
          spaceBetween={50}
          zoom
        >
          {chatMessageFileAttachments.map((attachment, i) => (
            <SwiperSlide className={classes.swiperSlide} key={attachment.id}>
              <Box height="100%">
                {attachment.mimeType.startsWith('image/') && (
                  <SwiperImage
                    alt={attachment.filename}
                    attachment={attachment}
                    isFullscreenDialog={isFullscreen}
                    src={getFilesApiUrl(attachment.key)}
                  />
                )}
                {!attachment.mimeType.startsWith('image/') && (
                  <div className={classes.iframeContainer}>
                    {/*
                        The bellow condition Lazy loads iframes.
                        Swiper doesn't automatically handle lazy loading for other tags
                        than images. We only render the iframe if its distance between
                        the active index is less or equal to
                        "SWIPER_LOAD_NEXT_PREV_AMOUNT".
                      */}
                    {Math.abs(i - activeIndex) <= SWIPER_LOAD_NEXT_PREV_AMOUNT && (
                      <iframe
                        loading="lazy"
                        className={classes.iframe}
                        src={getFilesApiUrl(attachment.key)}
                        title={attachment.filename}
                      />
                    )}
                  </div>
                )}
              </Box>
              {/* Required by the swiper component */}
              <div className="swiper-lazy-preloader" />
            </SwiperSlide>
          ))}
        </Swiper>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button
          component="a"
          disabled={isDownloading}
          onClick={() => download([currentAttachment ?? a])}
          startIcon={isDownloading ? <CircularProgress size="1.5rem"/> : <DownloadIcon/>}
        >
          {translate('actions.download')}
        </Button>
      </DialogActions>
    </ExtendedDialog>
  )
}

const SwiperImage = ({alt, attachment: a, isFullscreenDialog, src}) => {
  const {classes: styles} = useStyles({})
  return (
    /*
      The `maxWidth` condition allows a better display of images on large screens whose
      width is greater than the height.
      Without this condition they appear too small on large screens.
      Compare with these two images:
      h > w: https://unsplash.com/photos/1SPcjA1W8M4
      w > h: https://unsplash.com/photos/GncCCBNpWMU
     */
    <Container
      className={`swiper-zoom-container ${styles.swiperImageContainer}`}
      maxWidth={a.width > a.height ? 'xl' : 'sm'}
    >
      <img
        alt={alt}
        className={`
          ${styles.swiperImage}
          ${isFullscreenDialog ?
          styles.swiperImageDialogFullScreen :
          styles.swiperImageDialogNormalScreen}
        `}
        loading="lazy"
        src={src}
        width={a.width ? `${a.width}px` : undefined}
      />
    </Container>
  )
}

const SWIPER_LOAD_NEXT_PREV_AMOUNT = 2

const useStyles = makeStyles<any>()(theme => ({
  closeDialogButton: {
    '&.MuiButtonBase-root': {
      position: 'unset',
    },
  },
  dialogActions: {
    '& .MuiButtonBase-root': {
      marginTop: '8px',
    },
    borderTop: `solid 1px ${theme.palette.grey[300]}`,
  },
  dialogTitle: {
    alignItems: 'center',
    borderBottom: `solid 1px ${theme.palette.grey[300]}`,
    display: 'flex',
    margin: 0,
    paddingBottom: theme.remSpacing(1),
    paddingRight: theme.remSpacing(3.5),
    paddingTop: theme.remSpacing(1),
  },
  fullscreenButton: {
    [theme.breakpoints.down('lg')]: {
      display: 'none',
    },
    marginRight: theme.remSpacing(.5),
  },
  iframe: {
    border: 0,
    display: 'block', // https://stackoverflow.com/a/12726445/543875
    height: '100%',
    placeSelf: 'center',
    width: '100%',
  },
  iframeContainer: {
    height: '100%',
    paddingLeft: '3rem',
    paddingRight: '3rem',
  },
  root: {
    '& .MuiDialog-paper:not(.MuiDialog-paperFullScreen)': {
      height: '100%',
      maxHeight: 'min(800px, calc(100% - 64px))',
      maxWidth: 'min(1024px, calc(100% - 64px))',
      width: '100%',
    },
    '& .MuiDialogContent-root': {
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0,
    },
  },
  swiper: {
    '& .swiper-button-next': {
      color: theme.palette.secondary.main,
    },
    '& .swiper-button-prev': {
      color: theme.palette.secondary.main,
    },
    '& .swiper-lazy-loading': {
      display: 'none',
    },
    '& .swiper-lazy-preloader': {
      backgroundColor: theme.palette.background.paper,
      position: 'absolute',
    },
    height: '100%',
  },
  swiperImage: {
    maxHeight: '10vh',
    maxWidth: '100%',
    objectFit: 'contain',
  },
  swiperImageContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: 0,
  },
  swiperImageDialogFullScreen: {
    '@media (min-height: 300px)': {
      maxHeight: '40vh',
    },
    '@media (min-height: 400px)': {
      maxHeight: '50vh',
    },
    '@media (min-height: 500px)': {
      maxHeight: '60vh',
    },
    '@media (min-height: 600px)': {
      maxHeight: '70vh',
    },
    '@media (min-height: 700px)': {
      maxHeight: '80vh',
    },
  },
  swiperImageDialogNormalScreen: {
    '@media (min-height: 300px)': {
      maxHeight: '30vh',
    },
    '@media (min-height: 400px)': {
      maxHeight: '40vh',
    },
    '@media (min-height: 500px)': {
      maxHeight: '50vh',
    },
    '@media (min-height: 600px)': {
      maxHeight: '60vh',
    },
    '@media (min-height: 700px)': {
      maxHeight: '70vh',
    },
  },
  swiperSlide: {
    alignItems: 'center',
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
  },
  title: {
    flexGrow: 1,
  },
}))

interface FileAttachmentsSwiperDialogProps {
  chatMessageFileAttachments: ChatMessageFileAttachments[],
  isOpen: boolean,
  onOpen: (isOpen: boolean) => void,
  value: ChatMessageFileAttachments,
}

export default FileAttachmentsSwiperDialog
